import { useState } from 'react';
import ScrollToTop from '@/components/base/ScrollToTop';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { aboutData } from '@/mocks/about';

export default function AboutPage() {
  const [showStar, setShowStar] = useState<number | null>(null);

  const orgSchema = {
    name: aboutData.brandName,
    url: 'https://moguvideo.cn',
    description: aboutData.intro,
    foundingDate: '2018',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '北京市海淀区中关村科技园区创新大厦18层',
      addressLocality: '北京',
      addressCountry: 'CN',
    },
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+86-400-888-6666',
      contactType: 'customer service',
      availableLanguage: ['Chinese'],
    },
  };

  const breadcrumbSchema = {
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://moguvideo.cn/' },
      { '@type': 'ListItem', position: 2, name: '关于我们' },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <SchemaOrg type="Organization" data={orgSchema} />
      <SchemaOrg type="BreadcrumbList" data={breadcrumbSchema} />
      <SchemaOrg type="AboutPage" data={{ name: `关于${aboutData.brandName}`, description: aboutData.intro, url: 'https://moguvideo.cn/about' }} />
      <Navbar />
      <main className="min-h-screen pt-20 pb-10">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />

          <section className="mb-16">
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
              关于蘑菇视频在线观看
            </h1>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-6">{aboutData.intro}</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
              {[
                { label: '成立时间', value: aboutData.founded },
                { label: '总部地点', value: aboutData.headquarters },
                { label: '团队规模', value: aboutData.teamSize },
                { label: '累计用户', value: aboutData.users },
              ].map((item, i) => (
                <div key={i} className="bg-background-100/70 rounded-lg p-4 text-center">
                  <div className="font-label text-lg font-bold text-primary-600 mb-1">{item.value}</div>
                  <div className="text-xs text-foreground-500">{item.label}</div>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-16">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">使命与愿景</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="bg-background-100/70 rounded-lg p-5">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 mb-3">
                  <i className="ri-flag-line text-lg"></i>
                </div>
                <h3 className="font-heading text-base font-semibold text-foreground-950 mb-2">
                  <a href="#mission" className="hover:text-primary-600 transition-colors">我们的使命</a>
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{aboutData.mission}</p>
              </div>
              <div className="bg-background-100/70 rounded-lg p-5">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 mb-3">
                  <i className="ri-eye-line text-lg"></i>
                </div>
                <h3 className="font-heading text-base font-semibold text-foreground-950 mb-2">
                  <a href="#vision" className="hover:text-primary-600 transition-colors">我们的愿景</a>
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{aboutData.vision}</p>
              </div>
            </div>
          </section>

          <section className="mb-16">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">核心价值观</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {aboutData.values.map((val, i) => (
                <div key={i} className="bg-background-100/70 rounded-lg p-5">
                  <h3 className="font-heading text-sm font-semibold text-foreground-950 mb-2">{val.title}</h3>
                  <p className="text-xs text-foreground-500 leading-relaxed">{val.description}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-16">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">发展历程</h2>
            <div className="relative pl-6 border-l-2 border-primary-200 space-y-6">
              {aboutData.milestones.map((m, i) => (
                <div key={i} className="relative">
                  <div className="absolute -left-[29px] top-0.5 w-4 h-4 rounded-full bg-primary-500 border-2 border-background-50"></div>
                  <div className="bg-background-100/70 rounded-lg p-4">
                    <span className="font-label text-sm font-bold text-primary-600">{m.year}</span>
                    <p className="text-sm text-foreground-600 mt-1">{m.event}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-16">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">用户口碑</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {aboutData.reputation.map((rep, i) => (
                <div
                  key={i}
                  className="bg-background-100/70 rounded-lg p-5"
                  onMouseEnter={() => setShowStar(i)}
                  onMouseLeave={() => setShowStar(null)}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <img
                      src={rep.avatar}
                      alt={rep.user}
                      className="w-10 h-10 rounded-full object-cover"
                      loading="lazy"
                    />
                    <div>
                      <div className="text-sm font-semibold text-foreground-950">{rep.user}</div>
                      <div className="text-xs text-foreground-500">{rep.role}</div>
                    </div>
                    <div className="ml-auto flex items-center gap-0.5">
                      {Array.from({ length: rep.rating }).map((_, j) => (
                        <div key={j} className="w-3.5 h-3.5 flex items-center justify-center text-secondary-500">
                          <i className={`ri-star-fill text-xs ${showStar === i ? 'animate-pulse' : ''}`}></i>
                        </div>
                      ))}
                    </div>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-2">{rep.comment}</p>
                  <span className="text-xs text-foreground-400">{rep.date}</span>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-10">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6">联系我们</h2>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-1 space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-mail-line"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-950">电子邮箱</h4>
                    <p className="text-sm text-foreground-500">{aboutData.contact.email}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-phone-line"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-950">客服电话</h4>
                    <p className="text-sm text-foreground-500">{aboutData.contact.phone}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-map-pin-line"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-950">公司地址</h4>
                    <p className="text-sm text-foreground-500">{aboutData.contact.address}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-time-line"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-950">工作时间</h4>
                    <p className="text-sm text-foreground-500">{aboutData.contact.workingHours}</p>
                  </div>
                </div>
              </div>

              <div className="lg:col-span-2 bg-background-100/70 rounded-lg p-5 md:p-6">
                <h3 className="font-heading text-base font-semibold text-foreground-950 mb-4">发送消息</h3>
                <form
                  data-readdy-form
                  id="about-contact-form"
                  action="https://readdy.ai/api/form/d9sm4214ne17ei0sdjsg"
                  method="POST"
                  onSubmit={async (e) => {
                    e.preventDefault();
                    const form = e.currentTarget;
                    const formData = new FormData(form);
                    const honeypot = formData.get('mobile_alt') as string;
                    const statusEl = form.querySelector('.form-status');
                    if (honeypot && honeypot.trim() !== '') {
                      if (statusEl) statusEl.innerHTML = '<div class="text-sm text-green-600">消息已发送，我们会尽快回复您！</div>';
                      form.reset();
                      return;
                    }
                    formData.delete('mobile_alt');
                    try {
                      const response = await fetch('https://readdy.ai/api/form/d9sm4214ne17ei0sdjsg', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
                      });
                      const responseText = await response.text();
                      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null = null;
                      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
                      if (response.ok && parsed?.code === 'OK') {
                        if (statusEl) statusEl.innerHTML = '<div class="text-sm text-green-600">消息已发送，我们会尽快回复您！</div>';
                        form.reset();
                      } else {
                        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || '发送失败，请稍后重试';
                        if (statusEl) statusEl.innerHTML = `<div class="text-sm text-red-600">${serverMsg}</div>`;
                      }
                    } catch {
                      if (statusEl) statusEl.innerHTML = '<div class="text-sm text-red-600">发送失败，请稍后重试</div>';
                    }
                  }}
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                      <input
                        type="text"
                        id="contact-name"
                        name="name"
                        required
                        className="w-full px-3 py-2 text-sm border border-background-300/60 rounded-lg bg-background-50 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                      <input
                        type="email"
                        id="contact-email"
                        name="email"
                        required
                        className="w-full px-3 py-2 text-sm border border-background-300/60 rounded-lg bg-background-50 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                  </div>
                  <div className="mb-4">
                    <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">主题</label>
                    <input
                      type="text"
                      id="contact-subject"
                      name="subject"
                      required
                      className="w-full px-3 py-2 text-sm border border-background-300/60 rounded-lg bg-background-50 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入消息主题"
                    />
                  </div>
                  <div className="mb-4">
                    <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">消息内容</label>
                    <textarea
                      id="contact-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      className="w-full px-3 py-2 text-sm border border-background-300/60 rounded-lg bg-background-50 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                      placeholder="请输入您的消息内容（最多500字）"
                    ></textarea>
                    <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
                  </div>
                  <div className="antispam-field">
                    <label htmlFor="mobile_alt">备用手机</label>
                    <input
                      type="text"
                      id="mobile_alt"
                      name="mobile_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />
                  </div>
                  <button
                    type="submit"
                    className="px-6 py-2.5 rounded-lg bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
                  >
                    发送消息
                  </button>
                  <div className="form-status mt-3"></div>
                </form>
              </div>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </>
  );
}